import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Maximize2, MousePointerClick, X } from 'lucide-react';

import { ElementCommitments } from '@/features/commitments';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Skeleton } from '@/shared/ui';

import { accountStateClass, accountStateKey } from '../model/accountState';
import type { ElementDetail, OwnerView } from '../model/types';
import { useElement } from '../queries/useElement';
import styles from './ElementInspector.module.css';

/**
 * Panel-inspektor — szczegóły zaznaczonego elementu obok drzewka (wariant **D1** z makiety B0,
 * decyzja 159, werdykt usera 2026-08-26).
 *
 * Ten sam slot, w którym do 2026-08-26 stał panel zobowiązań E1 (decyzja 60), ale panel mówi
 * o **całym elemencie**: tytuł, typ, opis, właściciele z proweniencją i zobowiązania jako sekcja
 * (`ElementCommitments` — 1:1 z dawnym panelem, z tymi samymi akcjami). Drzewko zostaje na
 * miejscu i zachowuje szerokość na dwie rodziny liczb (decyzje 46/53).
 *
 * ⭐ Panel jest **skrótem**, nie ekranem: ⛔ bez sekcji relacji (słowo usera — relacje widać
 * w samym drzewku), właściciele tylko do odczytu, opis ucięty z „pokaż więcej". Edycja, zdejmowanie
 * właścicieli i pełne listy mieszkają na pełnym ekranie elementu (`/element/{id}`), do którego
 * prowadzi link w NAGŁÓWKU — tam będą dochodzić kolejne akcje (słowo usera: „później się
 * zastanowimy, co będzie można klikać w szczegółach, a co w pełnym panelu").
 *
 * ⚠ Przycisk „Wejdź" w nagłówku (decyzja 159) przeżył jeden dzień — user 2026-08-26 po pomiarze
 * okiem: usunąć, wejście robi dwuklik (i Shift+Enter), a na górę idzie link na pełny ekran
 * (decyzja 160). Wygląd panelu małego i dużego — osobna rozmowa, później.
 *
 * ⚠ Element bywa zaznaczony, a niewidoczny w drzewku (soczewka albo głębokość go schowały,
 * link z cudzego ekranu) — panel i tak go pokazuje, drzewko niczego nie podświetla, a podpowiedź
 * mówi, co go schowało.
 */

/** Ilu właścicieli pokazujemy bez rozwijania — po imporcie element ma ich bywa trzydziestu. */
const OWNERS_PREVIEW = 5;

/**
 * Próg, od którego opis dostaje „pokaż więcej". Liczony z treści, nie mierzony na ekranie —
 * pomiar (`scrollHeight`) wymagałby efektu układu przy każdej zmianie szerokości panelu,
 * a heurystyka myli się najwyżej o jedną linię i to w stronę pokazania przycisku.
 */
const DESCRIPTION_PREVIEW_CHARS = 280;
const DESCRIPTION_PREVIEW_LINES = 4;

interface ElementInspectorProps {
  /** `null` = nic nie zaznaczone: panel stoi i mówi, co zrobić. */
  elementId: number | null;
  /** Tytuł z drzewka, jeśli je zna — nagłówek maluje się od razu, przed detalem. */
  title?: string | undefined;
  type?: string | null | undefined;
  /** Czy zaznaczony element ma dziś jakikolwiek wiersz w drzewku. */
  inTree: boolean;
  /**
   * Żądanie „przewiń do zobowiązań": klik w liczby pracy przy wierszu albo stary link
   * `?panel=commitments`. Zmiana wartości = przewinięcie; `null` = nic nie przewijać.
   */
  commitmentsFocus: string | null;
  onClose: () => void;
}

export function ElementInspector(props: ElementInspectorProps) {
  const { t } = useTranslation();
  const { elementId } = props;

  return (
    <aside
      className={styles['inspector']}
      aria-label={t('graph.inspector.label')}
      // Atrybut dla układu wąskiego ekranu: pusty panel nie zabiera miejsca pod drzewkiem.
      {...(elementId === null ? { 'data-empty': '' } : {})}
    >
      {elementId === null ? (
        <EmptyHint />
      ) : (
        // Klucz przemontowuje treść przy zmianie elementu: rozwinięta historia zobowiązań
        // czy „pokaż więcej" opisu to stan TEGO elementu, nie panelu.
        <InspectorContent key={elementId} {...props} elementId={elementId} />
      )}
    </aside>
  );
}

/**
 * Pustka MÓWIĄCA (zasada usera 2026-08-08): pierwsze zdanie uczy kliknięcia, drugie wejścia —
 * to jedyne miejsce, w którym niewidzialny gest (dwuklik) jest napisany słowami. Bez glifów
 * klawiszy (spec 12B.7).
 */
function EmptyHint() {
  const { t } = useTranslation();

  return (
    <div className={styles['hint']}>
      <MousePointerClick size={28} aria-hidden="true" />
      <p className={styles['hintTitle']}>{t('graph.inspector.emptyTitle')}</p>
      <p className={styles['hintText']}>{t('graph.inspector.emptyClick')}</p>
      <p className={styles['hintText']}>{t('graph.inspector.emptyEnter')}</p>
    </div>
  );
}

function InspectorContent({
  elementId,
  title,
  type,
  inTree,
  commitmentsFocus,
  onClose,
}: ElementInspectorProps & { elementId: number }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const detail = useElement(elementId);

  const commitmentsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (commitmentsFocus !== null) {
      // Przewija najbliższego przewijalnego przodka (ciało panelu). Bez `behavior`: płynność
      // daje CSS `scroll-behavior`, który respektuje `prefers-reduced-motion`.
      commitmentsRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [commitmentsFocus]);

  const heading = title ?? detail.data?.title;
  const badgeType = type ?? detail.data?.type ?? null;
  const openCount = detail.data?.activeTaskCount ?? 0;

  return (
    <>
      <header className={styles['head']}>
        <div className={styles['headline']}>
          {heading === undefined ? (
            <Skeleton width="70%" height="1.3rem" />
          ) : (
            <h2 className={styles['host']} title={heading}>
              {heading}
            </h2>
          )}

          <Button variant="ghost" onClick={onClose} aria-label={t('graph.inspector.close')}>
            <X size={15} aria-hidden="true" />
          </Button>
        </div>

        {badgeType || openCount > 0 ? (
          <div className={styles['meta']}>
            {badgeType ? <span className={styles['badge']}>{badgeType}</span> : null}

            {/* Licznik zadań NA TYM elemencie, wszystkich userów, bez agregacji
                poddrzewa (decyzja 35) — ten sam, co na pełnym ekranie. */}
            {openCount > 0 ? (
              <span className={`${styles['badge']} ${styles['badgeWork']}`}>
                {t('common.count.commitment', { count: openCount })}
              </span>
            ) : null}
          </div>
        ) : null}

        {inTree ? null : <p className={styles['outside']}>{t('graph.inspector.outsideTree')}</p>}

        {/*
          Pełny ekran elementu U GÓRY (decyzja 160, słowo usera): to jedyna akcja panelu, więc
          ma być tam, gdzie człowiek patrzy najpierw. Prawdziwy link — środkowy klik i „otwórz
          w nowej karcie" działają.
        */}
        <div className={styles['headActions']}>
          <Link className={styles['fullScreen']} to={`/element/${elementId}`}>
            <Maximize2 size={15} aria-hidden="true" />
            {t('graph.inspector.openFull')}
          </Link>
        </div>
      </header>

      <div className={styles['body']}>
        {detail.isPending ? (
          <DetailSkeleton />
        ) : detail.isError ? (
          <div className={styles['failure']} role="alert">
            <p>{describeError(detail.error)}</p>
            <Button onClick={() => void detail.refetch()}>{t('common.action.retry')}</Button>
          </div>
        ) : (
          <>
            <DescriptionSection description={detail.data.description} />
            <OwnersSection owners={detail.data.owners} />
          </>
        )}

        {/*
          Zobowiązania idą OSOBNYM zapytaniem i nie czekają na detal — pad `/api/elements/{id}`
          nie może zabrać człowiekowi miejsca, w którym domyka robotę. Owijka istnieje dla
          przewinięcia: cel to cała sekcja, nie sam nagłówek.
        */}
        <div ref={commitmentsRef} className={styles['commitments']}>
          <ElementCommitments elementId={elementId} headingId="inspector-commitments" />
        </div>
      </div>
    </>
  );
}

/** Szkielet w kształcie tego, co przyjdzie (opis + właściciele), nie spinner. */
function DetailSkeleton() {
  return (
    <div className={styles['loading']} aria-busy="true">
      <Skeleton width="5rem" height="0.75rem" announce />
      <Skeleton height="0.9rem" />
      <Skeleton width="88%" height="0.9rem" />
      <Skeleton width="60%" height="0.9rem" />
      <Skeleton width="6rem" height="0.75rem" />
      <Skeleton width="70%" height="0.9rem" />
      <Skeleton width="55%" height="0.9rem" />
    </div>
  );
}

function isLongDescription(description: string): boolean {
  return (
    description.length > DESCRIPTION_PREVIEW_CHARS ||
    description.split('\n').length > DESCRIPTION_PREVIEW_LINES
  );
}

function DescriptionSection({ description }: { description: ElementDetail['description'] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const long = description !== null && isLongDescription(description);

  return (
    <section className={styles['section']} aria-labelledby="inspector-description">
      <h3 id="inspector-description" className={styles['sectionTitle']}>
        {t('graph.element.description')}
      </h3>

      {description ? (
        // Opis pisze człowiek — renderowany jako TEKST, nigdy HTML (spec 10B).
        <p
          className={`${styles['description']} ${
            long && !expanded ? styles['descriptionClamped'] : ''
          }`}
        >
          {description}
        </p>
      ) : (
        <p className={styles['empty']}>{t('graph.element.noDescription')}</p>
      )}

      {long ? (
        <button
          type="button"
          className={styles['textButton']}
          aria-expanded={expanded}
          onClick={() => setExpanded((previous) => !previous)}
        >
          {expanded ? t('graph.inspector.showLess') : t('graph.inspector.showMore')}
        </button>
      ) : null}
    </section>
  );
}

/**
 * Właściciele — TYLKO DO ODCZYTU. Dodanie, zdjęcie i wykluczenie (trzy różne rzeczy, spec 9.1)
 * zostają na pełnym ekranie: to akcje o skutkach w poddrzewie, nie coś do klikania przy okazji
 * porównywania sąsiadów.
 */
function OwnersSection({ owners }: { owners: readonly OwnerView[] }) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? owners : owners.slice(0, OWNERS_PREVIEW);
  const collapsible = owners.length > OWNERS_PREVIEW;

  return (
    <section className={styles['section']} aria-labelledby="inspector-owners">
      <div className={styles['sectionHead']}>
        <h3 id="inspector-owners" className={styles['sectionTitle']}>
          {t('graph.element.owners')}
        </h3>
        {owners.length > 0 ? <span className={styles['count']}>{owners.length}</span> : null}
      </div>

      {owners.length === 0 ? (
        <p className={styles['empty']}>{t('graph.element.noOwners')}</p>
      ) : (
        <ul className={styles['owners']}>
          {visible.map((owner) => (
            <li key={`${owner.ownerKind}-${owner.ownerId}`} className={styles['owner']}>
              <span className={styles['ownerKind']}>
                {owner.ownerKind === 'team' ? t('graph.owner.team') : t('graph.owner.user')}
              </span>
              <span
                className={
                  accountStateClass(owner.ownerState, styles, 'ownerNameOff') ?? styles['ownerName']
                }
              >
                {owner.displayName}
              </span>
              {/* ⛔ Kolor nigdy jako jedyny nośnik (dec. 58) — dopisek jedzie razem.
                  ⚠ Osobne zdanie dla konta czekającego: „wyłączone" byłoby o nim nieprawdą. */}
              {accountStateKey(owner.ownerState) ? (
                <span className={styles['provenance']}>{t(accountStateKey(owner.ownerState)!)}</span>
              ) : null}
              {/* Proweniencja (spec 9.1): bez „odziedziczone z Płatności" nazwa zespołu jest
                  dla człowieka nieweryfikowalna. „Dziedziczy w dół" tylko przy lokalnym —
                  przy odziedziczonym mówiłoby o cudzym przypisaniu u przodka. */}
              <span className={styles['provenance']}>
                {owner.isLocal
                  ? owner.inherit
                    ? t('graph.owner.inheritsDown')
                    : t('graph.owner.stopsHere')
                  : t('graph.owner.inheritedFrom', {
                      source: owner.sourceElementTitle ?? `#${owner.sourceElementId}`,
                    })}
              </span>
            </li>
          ))}
        </ul>
      )}

      {collapsible ? (
        <button
          type="button"
          className={styles['textButton']}
          aria-expanded={showAll}
          onClick={() => setShowAll((previous) => !previous)}
        >
          {showAll
            ? t('graph.inspector.showFewerOwners')
            : t('graph.inspector.showAllOwners', { count: owners.length })}
        </button>
      ) : null}
    </section>
  );
}
